'use client'

import { Menu, MenuButton, MenuItem, MenuList } from '@/components/Menu'
import {
  BotMessageSquareIcon,
  ChevronDown,
  CopyCheckIcon,
  DatabaseIcon,
  DatabaseZapIcon,
  FeatherIcon,
  LandmarkIcon,
  MegaphoneIcon,
  MessagesSquareIcon,
  ScaleIcon,
  ScanSearchIcon,
  Scroll,
  SearchIcon,
  SignatureIcon,
  SparklesIcon,
} from 'lucide-react'
import { desktop } from '@/styles/theme.css'
import LawIcon from '@/public/assets/law.icon.svg'
import LogoBackdropBrand from '@/public/assets/amiga-logo-backdrop-brand.icon.svg'
import LogoBackdropTeal from '@/public/assets/amiga-logo-backdrop-teal.icon.svg'
import { Box, Row, Stack } from '@christiankaindl/lyts'
import Link from 'next/link'
import { Menubar, PopoverArrow } from '@ariakit/react'
import { pathMappings } from '@/lib/paths'
import { urlify } from '@/lib/utils'
import Heading from '../Heading/Heading'
import Text from '../Text/Text'
import bg from '@/public/assets/amiga-bg.jpg'
import Image from 'next/image'

export default function NavBarMenus () {
  return (
    <Row gap='1px' className={desktop} asChild>
      <Menubar>
        <ProductsMenu />
        <EnterpriseMenu />
        <LegalMenu />
        <ResourcesMenu />
      </Menubar>
    </Row>
  )
}

function ProductsMenu () {
  return (
    <Menu>
      <MenuItem render={<MenuButton showOnHover />} style={{ fontWeight: 400 }}>
        Produkte{' '}
        <ChevronDown
          size={16}
          style={{ marginRight: -4, marginLeft: -4, marginBottom: -2 }}
        />
      </MenuItem>

      <MenuList
        shift={-128}
        style={{ maxWidth: 'unset', padding: 15, borderRadius: 30 }}
      >
        <PopoverArrow />
        <Row gap={0.5}>
          <Row
            style={{
              position: 'relative',
              zIndex: 1,
              padding: 24,
              borderRadius: 15,
              boxShadow: 'inset 0px 0px 1px rgb(0 0 0 / 0.3)',
              overflow: 'hidden',
            }}
          >
            <Stack xAlign='center'>
              <Heading h={3}>AmigaAI</Heading>
              <Text color='secondary' size='small' align='center'>
                Künstliche Intelligenz
                <br />
                für Juristen.
              </Text>
              {/* <Text size='small'>
                <Link href='/amiga'>
                  Mehr erfahren →
                </Link>
              </Text> */}
            </Stack>

            <Stack gap={0.25}>
              <RichMenuItem
                title='Regulatory Monitoring'
                description='All-in-one Regulatory Compliance'
                icon={LawIcon}
                href='/amiga/regulatory-monitoring'
              />
              <RichMenuItem
                title='Judikatur Monitoring'
                description='AI-powered Entscheidungs-Monitoring'
                icon={ScaleIcon}
                href='/amiga/judikatur-monitoring'
              />
              <RichMenuItem
                title='AmigaAI Summary'
                description='Zusammenfassungen für rechtliche Dokumente'
                icon={SparklesIcon}
                href='/amiga/judikatur-monitoring#summary'
              />
            </Stack>
            <Image
              priority
              src={bg}
              fill
              alt=''
              style={{
                objectFit: 'cover',
                objectPosition: 'center',
                zIndex: -1,
                opacity: 0.4,
                maskImage: 'linear-gradient(to top, transparent, white 30%)',
              }}
            />
          </Row>

          <div />

          <Stack gap={0.25}>
            <RichMenuItem
              title='Search API'
              description='State-of-the-art semantische Suche für Recht'
              icon={SearchIcon}
              href='/amiga/api#search'
            />
            <RichMenuItem
              title='Data API'
              description='Dokumente auf Bundes-, Landes- und EU-Ebene'
              icon={DatabaseIcon}
              href='/amiga/api#data'
            />
            <RichMenuItem
              title='Summary API'
              description='AmigaAI Summaries'
              icon={SparklesIcon}
              href='/amiga/api#summary'
            />
          </Stack>
        </Row>
      </MenuList>
    </Menu>
  )
}

function EnterpriseMenu () {
  return (
    <Menu>
      <MenuItem render={<MenuButton showOnHover />} style={{ fontWeight: 400 }}>
        Enterprise{' '}
        <ChevronDown
          size={16}
          style={{ marginRight: -4, marginLeft: -4, marginBottom: -2 }}
        />
      </MenuItem>

      <MenuList
        shift={-128}
        style={{ maxWidth: 'unset', padding: '30px', borderRadius: 30 }}
      >
        <PopoverArrow />
        <Row gap={1.5}>
          <Stack xAlign='center'>
            <Heading h={3}>Enterprise</Heading>
            <Text color='secondary' size='small' align='center'>
              Maßgeschneiderte Lösungen
              <br />
              für dein Unternehmen.
            </Text>
            <Text size='small'>
              <Link href='/enterprise'>Mehr erfahren →</Link>
            </Text>
          </Stack>

          <Stack gap={0.25}>
            <RichMenuItem
              title='IKS Integrationen'
              description='Nahtlose integration in interne Kontrollsysteme (IKS)'
              icon={CopyCheckIcon}
              iconBackdrop={LogoBackdropTeal}
              iconColor='#187977'
              href='/enterprise#iks-integration'
            />
            <RichMenuItem
              title='Legal LLM'
              description='GDPR-Compliant Large Large Modelle hosted in Austria'
              icon={BotMessageSquareIcon}
              iconBackdrop={LogoBackdropTeal}
              iconColor='#187977'
              href='/enterprise#fine-tuning'
            />
            <RichMenuItem
              title='Chat-Lösungen'
              description='Chatbots mit AmigaAI Technologie' // or "... mit internen Dokumenten"
              icon={MessagesSquareIcon}
              iconBackdrop={LogoBackdropTeal}
              iconColor='#187977'
              href='/enterprise#chat'
            />
          </Stack>

          <Stack gap={0.25} style={{ alignSelf: 'start' }}>
            <RichMenuItem
              title='Dokumentgenerierung'
              description='Automatisierte Erstellung anhand von internen Vorlagen'
              icon={SignatureIcon}
              iconBackdrop={LogoBackdropTeal}
              iconColor='#187977'
              href='/enterprise#document-generation'
            />
            <RichMenuItem
              title='Fine-tuning'
              description='Individuelle Anpassung von LLMs für präzisere Ergebnisse'
              icon={DatabaseZapIcon}
              iconBackdrop={LogoBackdropTeal}
              iconColor='#187977'
              href='/enterprise#fine-tuning'
            />
            <RichMenuItem
              title='Semantische Suche'
              description='State-of-the-art Suche für Recht'
              icon={ScanSearchIcon}
              iconBackdrop={LogoBackdropTeal}
              iconColor='#187977'
              href='/enterprise#semantic-search'
            />
          </Stack>
        </Row>
      </MenuList>
    </Menu>
  )
}

function LegalMenu () {
  const judikaturPaths = Object.values(pathMappings.judikatur.paths).filter(
    ({ hidden }) => !hidden,
  )

  return (
    <Menu>
      <MenuItem render={<MenuButton showOnHover />} style={{ fontWeight: 400 }}>
        Recht{' '}
        <ChevronDown
          size={16}
          style={{ marginRight: -4, marginLeft: -4, marginBottom: -2 }}
        />
      </MenuItem>
      <MenuList
        shift={-64}
        unmountOnHide={false}
        style={{ maxWidth: 'unset', padding: 15, borderRadius: 21 }}
      >
        <PopoverArrow />
        <Row>
          <Stack gap='1px'>
            <Link
              href='/bundesrecht'
              style={{
                fontSize: 'large',
                padding: '6px 12px',
                display: 'inline-block',
              }}
            >
              <b>Bundesrecht</b>
            </Link>
            <MenuItem
              icon={LawIcon}
              iconColor='#987C4D'
              href='/bundesrecht/bundesverfassungsgesetze'
            >
              <Row gap={0.5}>Verfassungsgesetze</Row>
            </MenuItem>
            <MenuItem
              icon={LawIcon}
              iconColor='#987C4D'
              href='/bundesrecht/bundesgesetze'
            >
              <Row gap={0.5}>Bundesgesetze</Row>
            </MenuItem>
            <MenuItem
              icon={Scroll}
              iconColor='#987C4D'
              href='/bundesrecht/verordnungen'
            >
              <Row gap={0.5}>Verordnungen</Row>
            </MenuItem>
            <MenuItem
              icon={FeatherIcon}
              iconColor='#987C4D'
              href='/bundesrecht/vertraege'
            >
              <Row gap={0.5}>Verträge</Row>
            </MenuItem>
            <MenuItem
              icon={MegaphoneIcon}
              iconColor='#987C4D'
              href='/bundesrecht/kundmachungen'
            >
              <Row gap={0.5}>Kundmachungen</Row>
            </MenuItem>
          </Stack>

          <div />

          <Stack gap='1px'>
            <Link
              href='/landesrecht'
              style={{
                fontSize: 'large',
                padding: '6px 12px',
                display: 'inline-block',
              }}
            >
              <b>Landesrecht</b>
            </Link>
            <MenuItem
              icon={LawIcon}
              iconColor='#987C4D'
              href='/landesrecht/landesverfassungsgesetze'
            >
              <Row gap={0.5}>Verfassungsgesetze</Row>
            </MenuItem>
            <MenuItem
              icon={LawIcon}
              iconColor='#987C4D'
              href='/landesrecht/landesgesetze'
            >
              <Row gap={0.5}>Bundesgesetze</Row>
            </MenuItem>
            <MenuItem
              icon={Scroll}
              iconColor='#987C4D'
              href='/landesrecht/verordnungen'
            >
              <Row gap={0.5}>Verordnungen</Row>
            </MenuItem>
            <MenuItem
              icon={FeatherIcon}
              iconColor='#987C4D'
              href='/landesrecht/vereinbarungen'
            >
              <Row gap={0.5}>Art. 15a Vereinbarungen</Row>
            </MenuItem>
            <MenuItem
              icon={MegaphoneIcon}
              iconColor='#987C4D'
              href='/landesrecht/kundmachungen'
            >
              <Row gap={0.5}>Kundmachungen</Row>
            </MenuItem>
          </Stack>

          <div />

          <Stack gap='1px' style={{ minWidth: 180 }}>
            <Link
              href='/judikatur'
              style={{
                fontSize: 'large',
                padding: '6px 12px',
                display: 'inline-block',
              }}
            >
              <b>Judikatur</b>
            </Link>
            {judikaturPaths.map(({ name }, index) => {
              if (index >= 5) return null

              return (
                <MenuItem
                  key={name}
                  icon={LandmarkIcon}
                  iconColor='#987C4D'
                  href={`/judikatur/${urlify(name)}`}
                >
                  <Row gap={0.5}>{name}</Row>
                </MenuItem>
              )
            })}
          </Stack>
        </Row>
      </MenuList>
    </Menu>
  )
}

function ResourcesMenu () {
  return (
    <Menu>
      <MenuItem render={<MenuButton showOnHover />} style={{ fontWeight: 400 }}>
        Themen{' '}
        <ChevronDown
          size={16}
          style={{ marginRight: -4, marginLeft: -4, marginBottom: -2 }}
        />
      </MenuItem>
      <MenuList unmountOnHide={false} style={{ padding: 15, borderRadius: 21 }}>
        <PopoverArrow />
        <div
          style={{
            fontSize: 'large',
            padding: '6px 12px',
            display: 'inline-block',
          }}
        >
          <b>Themen</b>
        </div>
        <MenuItem href='/blog'>
          <Row gap={0.5}>Blog</Row>
        </MenuItem>
        <MenuItem href='/themen/grundlagen-des-rechts'>
          <Row gap={0.5}>Grundlagen des Rechts</Row>
        </MenuItem>
        <MenuItem iconColor='#987C4D' href='/themen/schule'>
          <Row gap={0.5}>Schule</Row>
        </MenuItem>
        <MenuItem iconColor='#987C4D' href='/themen/studium'>
          <Row gap={0.5}>Studium</Row>
        </MenuItem>
        <MenuItem iconColor='#987C4D' href='/themen/strafrecht'>
          <Row gap={0.5}>Strafrecht</Row>
        </MenuItem>
      </MenuList>
    </Menu>
  )
}

function RichMenuItem ({
  title,
  description,
  icon: Icon,
  iconBackdrop: IconBackdrop = LogoBackdropBrand,
  iconColor = '#987C4D',
  href,
}) {
  return (
    <Row gap={0.5} asChild>
      <MenuItem
        href={href}
        style={{
          paddingTop: 6,
          paddingBottom: 6,
          maxWidth: '18em',
          borderRadius: 9,
        }}
      >
        <Box
          style={{ position: 'relative', width: 36, height: 36, flexShrink: 0 }}
          xAlign='center'
          yAlign='center'
        >
          <IconBackdrop
            height={36}
            width={36}
            style={{ position: 'absolute', top: 0, left: 0 }}
          />
          <Icon height={16} width={16} color={iconColor} />
        </Box>
        <Stack gap={0}>
          {title}
          <Text
            size='small'
            color='tertiary'
            style={{ textWrap: 'wrap', lineHeight: 1.25 }}
          >
            {description}
          </Text>
        </Stack>
      </MenuItem>
    </Row>
  )
}
